import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Box, Text, Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import PageIcon from '@atlaskit/icon/core/page';

import type { PageMode } from '@confluence/page-utils/entry-points/enums';
import {
	CommentActionType,
	CommentType,
	type CommentData,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n';
import {
	handleCommentsPanelNavigation,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils';
import { CommentsType } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import { useCommentsPanel } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel';
import { useUnreadPanelComments } from '@confluence/comments-panel/entry-points/useUnreadPanelComments';
import {
	type CommentThreadMouseEnterParams,
	handleCommentThreadMouseEnter,
	handleCommentThreadMouseLeave,
} from '@confluence/comments-common/entry-points/threadHelpers';
import { fg } from '@confluence/feature-gating';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments';
import { expValEquals, expVal } from '@confluence/feature-experiments';

import { ObjectComment } from './ObjectComment';
import { ObjectCommentReplies } from './ObjectCommentReplies';

export type ObjectCommentThreadProps = {
	parentComment: CommentData & {
		isFirstPageComment?: boolean;
	};
	supportedTopLevelActions: CommentAction[];
	pageMode: PageMode;
	flags?: FlagsStateContainer;
	shouldRenderCommentTypeSeparator?: boolean;
	isLastThread?: boolean;
	onLastThreadRendered?: () => void;
	isOffline?: boolean;
};

const commentThreadBoxStyles = xcss({
	listStyle: 'none',
	position: 'relative',
	paddingLeft: 'space.200',
	paddingRight: 'space.250',
	paddingTop: 'space.100', // On figma this is 200 but the object comment have 100px padding on top
	paddingBottom: 'space.200',
	margin: '0',
	display: 'flex',
	flexDirection: 'column',
	// Focus styles with outline - better containment
	':focus': {
		outlineOffset: 'space.negative.025',
	},
	':focus-visible': {
		outlineOffset: 'space.negative.025',
	},
});

const newCommentThreadBoxStyles = xcss({
	cursor: 'pointer',
});

const commentTypeSeperatorStyle = xcss({
	display: 'flex',
	gap: 'space.050',
	paddingTop: 'space.300',
	paddingRight: 'space.300',
	paddingBottom: 'space.150',
	paddingLeft: 'space.200',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
});

const commentBodyBoxStyles = xcss({
	listStyle: 'none',
	position: 'relative',
	marginRight: 'space.100',
});

const isCurrentUserSelectedCommentStyles = xcss({
	background: token('color.background.input.hovered'),
});

const isThreadPressedStyles = xcss({
	background: token('color.background.input.hovered'),
});

const i18n = defineMessages({
	commentTypeSeperatorText: {
		id: 'object-comments.comment.thread.type-seperator',
		defaultMessage: 'General comments',
		description: 'Text for the seperator of inline and general comment section',
	},
	threadContentAriaLabelWithName: {
		id: 'object-comments.thread.content.aria-label.with-name',
		defaultMessage: '{name}’s comment thread',
		description:
			'Aria-label for content associated with the comment thread, with the first comment author name',
	},
});

const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

// This component contains parent comment and its replies
export const ObjectCommentThread = ({
	parentComment,
	supportedTopLevelActions,
	pageMode,
	flags,
	shouldRenderCommentTypeSeparator,
	isLastThread,
	onLastThreadRendered,
	isOffline,
}: ObjectCommentThreadProps) => {
	const { formatMessage } = useIntl();

	const [
		{ currentlySelectedThreadKey, commentsMarkedAsRead },
		{ setCurrentlySelectedThreadKey, setCommentsInThreadToBeMarkedAsRead },
	] = useCommentsPanel();
	const { pageInfo } = usePageInfo();
	const { handleMarkCommentsAsRead } = useUnreadPanelComments();
	const { unreadCommentIDSet } = useUnreadCommentsState();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();
	const { isCommentsPanelShown } = useShowCommentsPanel();
	const isServer = useRenderServerPlaceholder();

	const threadKey = parentComment.id;

	// State
	const [isThreadHovered, setIsThreadHovered] = useState<boolean>(false);
	const [isThreadPressed, setIsThreadPressed] = useState<boolean>(false);
	const [hoveredCommentId, setHoveredCommentId] = useState<string | undefined>(undefined);
	const [commentIsFocused, setCommentIsFocused] = useState<boolean>(false);

	const focusedCommentId = useFocusedCommentIdQueryParam();
	const replyToComment = useReplyToCommentQueryParam();

	const commentRef = useRef<HTMLLIElement>(null);

	// Hook dependent constants
	const isCurrentSelectedComment = currentlySelectedThreadKey === threadKey;

	const { canAddComments } = useAddCommentPermissionCheck(pageInfo?.id ?? '', true);

	// Call handleMarkCommentsAsRead when commentsMarkedAsRead is updated
	useEffect(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		if (!isCurrentSelectedComment) {
			return;
		}
		if (Object.keys(commentsMarkedAsRead.general).length > 0) {
			handleMarkCommentsAsRead();
		}
	}, [commentsMarkedAsRead, handleMarkCommentsAsRead, isCurrentSelectedComment]);

	const pendingToMarkRef = useRef<Set<string>>(new Set());
	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Set up IntersectionObserver for scroll-based marking with improved debounce
	useEffect(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		if (!fg('confluence-fe-comment-scroll-to-read') || isServer) {
			return;
		}
		if (!commentRef.current) return;

		const handleIntersection = (entry: IntersectionObserverEntry) => {
			if (entry.isIntersecting && entry.intersectionRatio > 0) {
				// Only add IDs that are not already marked as read
				if (
					parentComment.isUnread &&
					!commentsMarkedAsRead[parentComment.type][threadKey]?.has(parentComment.id)
				) {
					pendingToMarkRef.current.add(parentComment.id);
				}
				parentComment.replies.forEach((reply) => {
					if (
						reply.isUnread &&
						!commentsMarkedAsRead[parentComment.type][threadKey]?.has(reply.id)
					) {
						pendingToMarkRef.current.add(reply.id);
					}
				});

				// Debounce: clear previous timer
				if (debounceTimeoutRef.current) {
					clearTimeout(debounceTimeoutRef.current);
				}
				// Set new timer
				debounceTimeoutRef.current = setTimeout(() => {
					const idsToMark = Array.from(pendingToMarkRef.current);
					if (idsToMark.length > 0) {
						setCommentsInThreadToBeMarkedAsRead({
							threadKey,
							commentIds: idsToMark,
							commentType: parentComment.type,
						});
						pendingToMarkRef.current.clear();
						handleMarkCommentsAsRead();
					}
				}, 2000);
			}
		};

		// eslint-disable-next-line no-restricted-syntax
		const observer = new IntersectionObserver(
			(entries) => {
				handleIntersection(entries[0]);
			},
			{
				threshold: [0, 1],
				rootMargin: '0px 0px -10% 0px',
			},
		);

		observer.observe(commentRef.current);

		return () => {
			observer.disconnect();
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, [
		parentComment.isUnread,
		parentComment.id,
		parentComment.type,
		parentComment.replies,
		threadKey,
		setCommentsInThreadToBeMarkedAsRead,
		commentsMarkedAsRead,
		handleMarkCommentsAsRead,
		isServer,
	]);

	useEffect(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		if (!fg('confluence-fe-comment-scroll-to-read')) {
			return;
		}
		if (!parentComment.isUnread) {
			return;
		}
		const targetCommentId = focusedCommentId || replyToComment;
		if (targetCommentId && targetCommentId === parentComment.id) {
			setCommentsInThreadToBeMarkedAsRead({
				threadKey,
				commentIds: [parentComment.id],
				commentType: parentComment.type,
			});
		}
	}, [
		parentComment.isUnread,
		parentComment.id,
		parentComment.type,
		threadKey,
		setCommentsInThreadToBeMarkedAsRead,
		focusedCommentId,
		replyToComment,
	]);

	const handleCommentThreadHoverProps: CommentThreadMouseEnterParams = useMemo(
		() => ({
			threadKey,
			setIsThreadHovered,
			setHoveredCommentId,
			pageMode,
		}),
		[threadKey, setIsThreadHovered, setHoveredCommentId, pageMode],
	);

	const handleMouseEnter = useCallback(
		(id?: string) => {
			handleCommentThreadMouseEnter({
				...handleCommentThreadHoverProps,
				id,
			});
		},
		[handleCommentThreadHoverProps],
	);

	const handleMouseLeave = useCallback(() => {
		handleCommentThreadMouseLeave(handleCommentThreadHoverProps);
	}, [handleCommentThreadHoverProps]);

	const handleFocus = useCallback(
		(event: React.FocusEvent) => {
			event.stopPropagation();
			setCommentIsFocused(true);
		},
		[setCommentIsFocused],
	);

	const handleBlur = useCallback(
		(event: React.FocusEvent) => {
			// Only clear focus if we're not focusing a child element
			if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget as Node)) {
				setCommentIsFocused(false);
			}
		},
		[setCommentIsFocused],
	);

	const unreadParentCommentAlreadyMarkedAsReadRef = useRef(false);

	if (
		isCurrentSelectedComment &&
		!unreadParentCommentAlreadyMarkedAsReadRef.current &&
		parentComment.isUnread &&
		!fg('confluence_frontend_unread_comments_fixes')
	) {
		setCommentsInThreadToBeMarkedAsRead({
			threadKey,
			commentIds: [parentComment.id],
			commentType: parentComment.type,
		});
		unreadParentCommentAlreadyMarkedAsReadRef.current = true;
	}

	const isCommentRemovedByAnotherUser = parentComment?.wasRemovedByAnotherUser;
	const isResolvedByAnotherUser =
		isCommentRemovedByAnotherUser &&
		isCommentRemovedByAnotherUser === CommentActionType.RESOLVE_COMMENT_THREAD;

	// if comment thread is resolved, mark it as read exactly once
	useEffect(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		if (isResolvedByAnotherUser) {
			const unreadCommentIds = [];

			if (parentComment.isUnread) {
				unreadCommentIds.push(parentComment.id);
			}

			const unreadReplies = parentComment.replies.filter((reply) => reply.isUnread);
			if (unreadReplies.length > 0) {
				unreadCommentIds.push(...unreadReplies.map((reply) => reply.id));
			}

			if (unreadCommentIds.length > 0) {
				setCommentsInThreadToBeMarkedAsRead({
					threadKey,
					commentIds: unreadCommentIds,
					commentType: parentComment.type,
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleCommentTheadMouseDown = () => {
		/**
		 * Limiting executions of this function because updating isThreadPressed
		 * rerenders every component in this tree. Most notably, a user clicking into
		 * the reply comment editor will call this function every time (and really any sibling component).
		 * 1. isThreadPressed is only used as a style variable alongside isCurrentSelectedComment
		 * 2. if clicking doesn't change threadKey then currentlySelectedThreadKey never changes either
		 * which are the two variables isCurrentSelectedComment compares. We should only call this function
		 * if threadKey has changed.
		 */
		if (!isCurrentSelectedComment) {
			setIsThreadPressed(true);
			setCurrentlySelectedThreadKey(threadKey);
		}
	};

	useEffect(() => {
		const parentIdOfSelectedReply = parentComment?.replies?.find(
			(reply) => reply.id === currentlySelectedThreadKey,
		);
		if (isCurrentSelectedComment || parentIdOfSelectedReply) {
			setIsThreadPressed(true);
		} else {
			setIsThreadPressed(false);
		}
		return;
	}, [currentlySelectedThreadKey, isCurrentSelectedComment, parentComment?.replies]);

	useEffect(() => {
		if (isLastThread && onLastThreadRendered) {
			// isLastThread should only be evaluated when it's a boolean value
			// Currently, its being incorrectly evaluated when passed as a function,
			// which is leading to unexpected behavior in the onLastThreadRendered callback
			if (typeof isLastThread === 'boolean') {
				onLastThreadRendered();
			}
		}
	}, [isLastThread, onLastThreadRendered]);

	const objectCommentThreadHandler = () => {
		setCurrentlySelectedThreadKey(threadKey);

		// Use unified navigation if feature gate is enabled and comments panel is shown
		if (isCommentsPanelShown && fg('confluence_comments_footer_panel_syn_scroll')) {
			void handleCommentsPanelNavigation({
				annotationId: threadKey,
				commentType: CommentsType.FOOTER,
				scrollBehavior: ScrollBehavior.SMOOTH,
				block: 'center',
				context: NavigationContext.PANEL,
			});
		} else {
			// Fallback to legacy scrolling
			scrollCommentsPanelToThread({
				annotationId: threadKey,
				commentType: CommentType.GENERAL,
			});
		}
	};

	return (
		<>
			{shouldRenderCommentTypeSeparator && (
				<Flex
					xcss={commentTypeSeperatorStyle}
					as="li"
					testId="comment-section-separator"
					alignItems="center"
				>
					<PageIcon
						testId="general-comment-page-icon"
						color={token('color.icon.subtlest')}
						label={formatMessage(i18n.commentTypeSeperatorText)}
					/>
					<Text color="color.text.subtlest" weight="medium">
						{formatMessage(i18n.commentTypeSeperatorText)}
					</Text>
				</Flex>
			)}
			{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
			<Box
				ref={commentRef}
				id={`${LocationValue.FOOTER}-comment-thread-${threadKey}-container`}
				testId={`${LocationValue.FOOTER}-comment-thread-${threadKey}-container`}
				xcss={[
					commentThreadBoxStyles,
					isCurrentSelectedComment && isCurrentUserSelectedCommentStyles,
					!isCurrentSelectedComment && isThreadPressed && isThreadPressedStyles,
					newCommentThreadBoxStyles,
				]}
				onClick={objectCommentThreadHandler}
				onMouseDown={handleCommentTheadMouseDown}
				onMouseUp={() => setIsThreadPressed(false)}
				onFocus={handleFocus}
				onBlur={handleBlur}
				// TODO - COMMENTS-4934: Clean up confluence_frontend_comments_a11y_kb_exp FG
				{...(expVal('confluence_frontend_comments_a11y_kb_exp', 'isEnabled', false)
					? {
							tabIndex: 0,
							'aria-label': formatMessage(i18n.threadContentAriaLabelWithName, {
								name: parentComment.author?.displayName || formatMessage(commentsi18n.anonymous),
							}),
						}
					: {})}
			>
				<Box
					testId={`${LocationValue.FOOTER}-comment-thread-${parentComment.id}-parent`}
					xcss={commentBodyBoxStyles}
					onMouseEnter={() => handleMouseEnter(parentComment.id)}
					onMouseLeave={handleMouseLeave}
				>
					<ObjectComment
						comment={parentComment}
						supportedTopLevelActions={supportedTopLevelActions}
						pageInfo={pageInfo}
						pageMode={pageMode}
						isUnread={
							fg('confluence_frontend_unread_comments_fixes')
								? unreadCommentIDSet.has(parentComment.id)
								: !commentsMarkedAsRead[parentComment.type][threadKey]?.has(parentComment.id) &&
									parentComment.isUnread
						}
						isHovered={
							hoveredCommentId === parentComment.id ||
							(commentIsFocused && fg('confluence_comments_hover_state_on_focused_comment'))
						}
						threadKey={threadKey}
						flags={flags}
						hideBranchingStyle={
							expValEquals('confluence_comments_tuneup', 'isEnabled', true)
								? parentComment.replies.length === 0
								: (!parentComment.isOpen || !canAddComments) && parentComment.replies.length === 0
						}
						canAddComments={canAddComments}
						isThreadHovered={isThreadHovered}
						isOffline={isOffline}
					/>
				</Box>
				<ObjectCommentReplies
					parentComment={parentComment}
					pageInfo={pageInfo}
					isCurrentSelectedComment={isCurrentSelectedComment}
					hoveredCommentId={hoveredCommentId ?? ''}
					pageMode={pageMode}
					parentCommentId={parentComment.id}
					handleMouseEnter={handleMouseEnter}
					handleMouseLeave={handleMouseLeave}
					canAddComments={canAddComments}
					isOffline={isOffline}
					flags={flags}
				/>
			</Box>
		</>
	);
};
